import {
    computed, watch, type ComputedRef,
} from 'vue';
import { useLiveTableStore } from '@/stores/powerMarkets/liveTableStore.ts';
import { notificationsApi } from '@/api/powerMarkets/notificationsApi.ts';
import { useNotificationsActions } from '@/actions/powerMarkets/notificationsActions.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { IOrderedSettings } from '@/interfaces/general/IOrderedSettings.ts';
import { ISubscriptionSettings } from '@/interfaces/powerMarkets/notifications/ISubscriptionSettings.ts';
import { useSignalRLifecycle } from '@/realTimeConnection/SignalRLifecycle.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';

let hasInitialisedNotifications = false;

interface IUseEnactNotifications {
    defaultCountry: ComputedRef<string>;
    hasNotificationsTable: ComputedRef<boolean>;
    initialiseNotifications: () => Promise<void>;
    markNotificationsSeen: () => void;
    notificationsDisabled: ComputedRef<boolean>;
    subscriptions: ComputedRef<IOrderedSettings<ISubscriptionSettings>>;
    unseenNotifications: ComputedRef<number>;
}

export function useEnactNotifications (): IUseEnactNotifications {
    const liveTableStore = useLiveTableStore();
    const userSettingsStore = useUserSettingsStore();
    const signalRLifecycle = useSignalRLifecycle();
    const userAccessStore = useUserAccessStore();
    const notificationsActions = useNotificationsActions();

    const notificationsDisabled = computed<boolean>(() => !userAccessStore.enactPageAccess.notifications.hasAccess);
    const userId = computed<string>(() => userSettingsStore.userSettings.otherSettings.data.userId);
    const subscriptions = computed<IOrderedSettings<ISubscriptionSettings>>(() => userSettingsStore.userSettings.subscriptionsWithOrder);
    const defaultCountry = computed(() => userSettingsStore.userCountry);
    const hasNotificationsTable = computed<boolean>(() => !!liveTableStore.storeTables.Notifications);

    const unseenNotifications = computed<number>(() => {
        const storeTables = liveTableStore.storeTables;
        return storeTables.Notifications ? storeTables.Notifications.data.filter((notification) => {
            if ('value' in notification.row.Unseen) return notification.row.Unseen.value === 'False';

            if ('values' in notification.row.Unseen) {
                return notification.row.Unseen.values[0] === 'False';
            }
            return false;
        }).length : 0;
    });

    async function initialiseNotifications (): Promise<void> {
        if (hasInitialisedNotifications || notificationsDisabled.value || !userId.value) return;

        try {
            const response = await notificationsApi.getLiveTableNotifications(userId.value);
            liveTableStore.addTableToStore('Notifications', response.data);
            hasInitialisedNotifications = true;
        } finally {
            signalRLifecycle.joinGroup('module', `Notifications${userId.value}`);
        }
    }

    function markNotificationsSeen (): void {
        if (notificationsDisabled.value) return;
        notificationsActions.markNotificationsSeen();
    }

    watch(() => [notificationsDisabled.value, userId.value], () => {
        initialiseNotifications();
    }, { immediate: true });

    return {
        defaultCountry,
        hasNotificationsTable,
        initialiseNotifications,
        markNotificationsSeen,
        notificationsDisabled,
        subscriptions,
        unseenNotifications,
    };
}
